import { Controller } from '@hotwired/stimulus';

/**
 * Export Trigger Controller
 *
 * Intercepts the click on a synchronous-export trigger button (ZIP, CSV, XLSX),
 * opens the shared `Ui::ExportProgressModalComponent`, fetches the export URL
 * via `fetch` + Blob, and triggers a client-side download. On non-2xx / redirect
 * (e.g. over-cap signaling that renders a `flash[:alert]`), it follows the
 * response URL so the server-rendered flash surfaces normally.
 *
 * The Cancel button inside the modal calls `cancel()` which aborts the in-flight
 * fetch via `AbortController` and closes the modal.
 *
 * Targets:
 * - (none — the modal's targets belong to its own DOM and are queried via
 *   `document.getElementById(modalId)` to support buttons living outside the modal)
 *
 * Values:
 * - url: The export endpoint to fetch
 * - count: Number of documents being exported (drives pluralisation)
 * - label: Headline label inside the modal body (e.g. "Preparation de votre archive")
 * - modalId: DOM id of the `Ui::ExportProgressModalComponent` to open/close
 *
 * Actions:
 * - start(event): Opens the modal and kicks off the fetch+download
 * - cancel(): Aborts the fetch and closes the modal
 *
 * @example
 * <%= render Ui::ButtonComponent.new("Exporter en ZIP",
 *       variant: :secondary,
 *       icon: :download,
 *       data: { controller: "export-trigger",
 *               action: "click->export-trigger#start",
 *               export_trigger_url_value: documents_export_path,
 *               export_trigger_count_value: 42,
 *               export_trigger_label_value: "Preparation de votre archive",
 *               export_trigger_modal_id_value: "documents-export-progress" }) %>
 *
 * <%= render Ui::ExportProgressModalComponent.new(id: "documents-export-progress") %>
 */
export default class extends Controller {
  static values = {
    url: String,
    count: Number,
    label: String,
    modalId: String,
  };

  /**
   * Click handler on the trigger button. Opens the modal, fetches the export
   * URL, and triggers a Blob download. On non-2xx response, navigates to the
   * response URL so the server flash renders.
   *
   * @param {Event} event
   */
  async start(event) {
    event.preventDefault();
    if (event.currentTarget.disabled) return;

    this.abortController = new AbortController();
    this.populateModal();
    this.openModal();

    try {
      const response = await fetch(this.urlValue, {
        signal: this.abortController.signal,
        redirect: 'follow',
      });

      if (response.redirected || !response.ok) {
        this.closeModal();
        window.location.href = response.url;
        return;
      }

      const blob = await response.blob();
      const filename =
        this.parseFilename(response.headers.get('Content-Disposition')) || 'export.zip';
      this.triggerDownload(blob, filename);
      this.closeModal();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.closeModal();
      alert("L'export a echoue. Reessayez ou contactez le support si le probleme persiste.");
    }
  }

  /**
   * Cancel handler — aborts the in-flight fetch and closes the modal. Wired to
   * the Annuler button inside `Ui::ExportProgressModalComponent`.
   */
  cancel() {
    this.abortController?.abort();
    this.closeModal();
  }

  // -- private helpers --

  /**
   * Populates the modal's label and pluralised count from this controller's
   * values. Called immediately before opening the modal.
   */
  populateModal() {
    const modal = document.getElementById(this.modalIdValue);
    if (!modal) return;
    const label = modal.querySelector("[data-export-progress-target='label']");
    const count = modal.querySelector("[data-export-progress-target='count']");
    if (label) label.textContent = this.labelValue || 'Preparation de votre archive';
    if (count) count.textContent = `${this.countValue} document${this.countValue > 1 ? 's' : ''}`;
  }

  openModal() {
    if (typeof window.openModal === 'function') {
      window.openModal(this.modalIdValue);
    }
  }

  closeModal() {
    if (typeof window.closeModal === 'function') {
      window.closeModal(this.modalIdValue);
    }
  }

  /**
   * Forces a client-side download of the given Blob with the given filename.
   * Revokes the ObjectURL on a short delay to give the browser time to start
   * the download.
   *
   * @param {Blob} blob
   * @param {string} filename
   */
  triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }

  /**
   * Parses the filename out of a `Content-Disposition` header.
   * Returns `null` when the header is missing or malformed.
   *
   * @param {string|null} contentDisposition
   * @returns {string|null}
   */
  parseFilename(contentDisposition) {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
  }
}
